import React, { useState } from 'react';
import { 
  Users, Search, Filter, Download, Edit, Trash, Eye, Plus, 
  ChevronLeft, ChevronRight, CheckCircle, XCircle, UserCheck, UserX
} from 'lucide-react';

// Datos de ejemplo para usuarios
const mockUsers = [
  {
    id: '1',
    name: 'Ana García',
    email: 'ana.garcia@example.com',
    role: 'product-manager',
    status: 'active',
    plan: 'Pro',
    joinDate: '15/03/2025',
    lastLogin: '10/05/2025'
  },
  {
    id: '2',
    name: 'Carlos Martínez',
    email: 'carlos.martinez@example.com',
    role: 'developer',
    status: 'active',
    plan: 'Premium',
    joinDate: '22/02/2025',
    lastLogin: '09/05/2025'
  },
  {
    id: '3',
    name: 'Laura Sánchez',
    email: 'laura.sanchez@example.com',
    role: 'entrepreneur',
    status: 'inactive',
    plan: 'Free',
    joinDate: '05/01/2025',
    lastLogin: '15/03/2025'
  }
];

const UsersManagement = () => {
  const [users, setUsers] = useState(mockUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPlan, setSelectedPlan] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const usersPerPage = 5;

  // Filtrar usuarios
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus;
    const matchesPlan = selectedPlan === 'all' || user.plan === selectedPlan;
    
    return matchesSearch && matchesRole && matchesStatus && matchesPlan;
  });

  // Paginación
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Cambiar de página
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Formatear rol para mostrar
  const formatRole = (role: string) => {
    const roles: Record<string, string> = {
      'product-manager': 'Product Manager',
      'developer': 'Desarrollador',
      'entrepreneur': 'Emprendedor',
      'tech-entrepreneur': 'Tech Entrepreneur',
      'admin': 'Administrador'
    };
    return roles[role] || role;
  };

  // Formatear estado para mostrar
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Activo
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircle className="h-3 w-3 mr-1" />
            Inactivo
          </span>
        );
      case 'suspended':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <UserX className="h-3 w-3 mr-1" />
            Suspendido
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  // Formatear plan para mostrar
  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'Free':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Gratuito
          </span>
        );
      case 'Pro':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Pro
          </span>
        );
      case 'Premium':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            Premium
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {plan}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Gestión de Usuarios</h1>
        <button 
          onClick={() => setShowAddUserModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
        >
          <Plus className="h-4 w-4 mr-2" />
          Añadir Usuario
        </button>
      </div>

      {/* Filtros y búsqueda */}
      <div className="dark-card p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar usuarios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-foreground/50" />
            </div>
          </div>
          <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
            <div className="flex items-center">
              <Filter className="h-5 w-5 text-foreground/50 mr-2" />
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="border border-border rounded-lg bg-secondary/30 text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                <option value="all">Todos los roles</option>
                <option value="product-manager">Product Manager</option>
                <option value="developer">Desarrollador</option>
                <option value="entrepreneur">Emprendedor</option>
                <option value="tech-entrepreneur">Tech Entrepreneur</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div className="flex items-center">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="border border-border rounded-lg bg-secondary/30 text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                <option value="all">Todos los estados</option>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="suspended">Suspendido</option>
              </select>
            </div>
            <div className="flex items-center">
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="border border-border rounded-lg bg-secondary/30 text-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                <option value="all">Todos los planes</option>
                <option value="Free">Gratuito</option>
                <option value="Pro">Pro</option>
                <option value="Premium">Premium</option>
              </select>
            </div>
            <button className="inline-flex items-center px-3 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-secondary/50">
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de usuarios */}
      <div className="dark-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-secondary/30">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-foreground/70 uppercase tracking-wider">
                  Usuario
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-foreground/70 uppercase tracking-wider">
                  Rol
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-foreground/70 uppercase tracking-wider">
                  Estado
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-foreground/70 uppercase tracking-wider">
                  Plan
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-foreground/70 uppercase tracking-wider">
                  Fecha de registro
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-foreground/70 uppercase tracking-wider">
                  Último acceso
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-foreground/70 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {currentUsers.map((user) => (
                <tr key={user.id} className="hover:bg-secondary/10">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center text-accent font-medium">
                        {user.name.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-foreground">{user.name}</div>
                        <div className="text-sm text-foreground/70">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-foreground">{formatRole(user.role)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPlanBadge(user.plan)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground/70">
                    {user.joinDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground/70">
                    {user.lastLogin}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button 
                        onClick={() => setSelectedUser(user.id)}
                        className="text-foreground/70 hover:text-foreground"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button className="text-foreground/70 hover:text-foreground">
                        <Edit className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => setUserToDelete(user.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className="px-6 py-4 flex items-center justify-between border-t border-border">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md ${
                currentPage === 1
                  ? 'text-foreground/50 cursor-not-allowed'
                  : 'text-foreground hover:bg-secondary/30'
              }`}
            >
              Anterior
            </button>
            <button
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md ${
                currentPage === totalPages
                  ? 'text-foreground/50 cursor-not-allowed'
                  : 'text-foreground hover:bg-secondary/30'
              }`}
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-foreground/70">
                Mostrando <span className="font-medium">{indexOfFirstUser + 1}</span> a{' '}
                <span className="font-medium">
                  {Math.min(indexOfLastUser, filteredUsers.length)}
                </span>{' '}
                de <span className="font-medium">{filteredUsers.length}</span> resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-background text-sm font-medium ${
                    currentPage === 1
                      ? 'text-foreground/50 cursor-not-allowed'
                      : 'text-foreground hover:bg-secondary/30'
                  }`}
                >
                  <span className="sr-only">Anterior</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium ${
                      currentPage === number
                        ? 'bg-accent text-white'
                        : 'bg-background text-foreground hover:bg-secondary/30'
                    }`}
                  >
                    {number}
                  </button>
                ))}
                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-background text-sm font-medium ${
                    currentPage === totalPages
                      ? 'text-foreground/50 cursor-not-allowed'
                      : 'text-foreground hover:bg-secondary/30'
                  }`}
                >
                  <span className="sr-only">Siguiente</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;